import { 
    getSupervisorsForWeek, 
    getBatchIDs, 
    getNextBatchNumber,
    getEmployeesBySupervisor,
    exportPayrollDataPaint,
    exportPayrollDataDrywall,
    getBatchReport,
    printTimecards
  } from "../api/v1BatchExport";
  import { Supervisor, EmployeeTimeSheet } from "../interfaces/V1Interface";
  
  // Function to get supervisors for a specific week
  const fetchSupervisors = async (timeSheetDate: string, sdate: string, edate: string): Promise<Supervisor[]> => {
      try {
          // Make the API request to get supervisors
          const response = await getSupervisorsForWeek(timeSheetDate, sdate, edate);
          const supervisors = response.data;
  
          // Log the results for debugging purposes
          console.log("Fetched supervisors:", supervisors);
  
          // Check if we got results
          if (!supervisors || supervisors.length === 0) {
              throw new Error("No supervisors found for the given date range.");
          }
  
          return supervisors;
  
      } catch (err) {
          console.error("Error in fetchSupervisors", err);
          throw err;
      }
  };
  
  // Function to get and prepare the next batch ID
  const prepareBatchId = async (batchPrefix: string): Promise<string> => {
      try {
          // Get existing batch IDs
          const batchIds = await getBatchIDs(batchPrefix);
          console.log("Existing batch IDs:", batchIds);
  
          // Determine the next available batch number
          const nextNumber = getNextBatchNumber(batchIds);
          const fullBatchId = `${batchPrefix} ${nextNumber}`;
  
          console.log("Next batch ID to use:", fullBatchId);
          return fullBatchId;
  
      } catch (err) {
          console.error("Error in prepareBatchId", err);
          throw err;
      }
  };
  
  // Function to get employees by supervisor
  const fetchEmployees = async (
      supervisorId: string,
      sdate: string,
      edate: string,
      timeSheetDate: string = "",
      search: string = "",
      conditionId: string = "0",
      pageNumber: number = 0
  ): Promise<EmployeeTimeSheet[]> => {
      try {
          // Make the API request to get employees
          const employees = await getEmployeesBySupervisor(
              supervisorId,
              sdate,
              edate,
              timeSheetDate,
              search,
              conditionId,
              pageNumber
          );
  
          console.log("Fetched employees:", employees);
  
          if (!employees || employees.length === 0) {
              throw new Error("No employees found for the given supervisor and date range.");
          }
  
          return employees;
  
      } catch (err) {
          console.error("Error in fetchEmployees", err);
          throw err;
      }
  };
  
  // Function to export payroll data (paint)
    const exportPaintPayroll = async (
        batchId: string,
        supervisorId: string,
        startDate: string,
        endDate: string,
        employeeIDs?: string[]
    ): Promise<any> => {
        try {
        console.log("Exporting paint payroll data...");
        const result = await exportPayrollDataPaint(batchId, supervisorId, startDate, endDate, employeeIDs);
    
        if (!result) {
            throw new Error("Paint payroll export failed.");
        }
    
        console.log("Paint payroll export successful:", result);
        return result;
        } catch (err) {
        console.error("Error in exportPaintPayroll", err);
        throw err;
        }
    };
    
    // Function to export payroll data (drywall)
    const exportDrywallPayroll = async (
        batchId: string,
        supervisorId: string,
        startDate: string,
        endDate: string,
        employeeIDs?: string[]
    ): Promise<any> => {
        try {
        console.log("Exporting drywall payroll data...");
        const result = await exportPayrollDataDrywall(batchId, supervisorId, startDate, endDate, employeeIDs);
    
        if (!result) {
            throw new Error("Drywall payroll export failed.");
        }
    
        console.log("Drywall payroll export successful:", result);
        return result;
        } catch (err) {
        console.error("Error in exportDrywallPayroll", err);
        throw err;
        }
    };  
  
  // Function to get batch reports
  const fetchBatchReports = async (batchId: string): Promise<{regularReport: any, ddReport: any}> => {
      try {
          console.log("Fetching batch reports...");
          const reports = await getBatchReport(batchId);
  
          if (!reports.regularReport || !reports.ddReport) {
              throw new Error("Failed to fetch one or both batch reports.");
          }
  
          console.log("Batch reports fetched successfully");
          return reports;
  
      } catch (err) {
          console.error("Error in fetchBatchReports", err);
          throw err;
      }
  };

// Function to print timecards for a supervisor
const fetchPrintTimecards = async (
    supervisorId: string,
    startDate: string,
    endDate: string
): Promise<string> => {
    try {
        console.log("Printing timecards for supervisor:", supervisorId);
        const result = await printTimecards(supervisorId, startDate, endDate);

        if (!result || result.responseCode !== 200 || !result.status) {
            throw new Error("Failed to generate timecards. Please try again.");
        }

        if (!result.data) {
            throw new Error("No PDF data received from server.");
        }

        console.log("Timecards generated successfully:", result);
        return result.data;
    } catch (err) {
        console.error("Error in fetchPrintTimecards", err);
        throw err;
    }
};

export { 
    fetchSupervisors,
    prepareBatchId,
    fetchEmployees,
    exportPaintPayroll,
    exportDrywallPayroll,
    fetchBatchReports,
    fetchPrintTimecards
};